import React, { useEffect, useState } from 'react'

import cx from 'classnames'
import { post } from 'lib/api'
import { Input } from 'lib/components/Input'
import { ValidationError } from 'lib/types/errors'
import getError from 'lib/utils/getError'
import { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { Link, useSearchParams } from 'react-router-dom'

type Step = 'email' | 'otp' | 'reset' | 'success'

type ApiErrorResponse = {
  error?: {
    message?: string
  }
  errors?: Array<Partial<ValidationError>>
}

type ActionButtonProps = {
  children: React.ReactNode
  disabled?: boolean
  loading?: boolean

  onClick: () => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RESEND_SECONDS = 60

const isStep = (value: string | null): value is Step =>
  value === 'email' ||
  value === 'otp' ||
  value === 'reset' ||
  value === 'success'

const getApiValidationErrors = (
  error: AxiosError<ApiErrorResponse>
): ValidationError[] => {
  const data = error.response?.data

  if (!data?.errors || !Array.isArray(data.errors)) {
    return []
  }

  return data.errors
    .filter(
      (validationError): validationError is Partial<ValidationError> =>
        typeof validationError?.message === 'string'
    )
    .map((validationError) => ({
      field: validationError.field || 'general',
      message: validationError.message || 'Bir hata oluştu',
      rule: validationError.rule || 'server',
    }))
}

const getApiErrorMessage = (
  error: AxiosError<ApiErrorResponse>,
  fallbackMessage = 'Bir hata oluştu'
) => {
  const data = error.response?.data

  if (typeof data?.error?.message === 'string' && data.error.message) {
    return data.error.message
  }

  const errors = data?.errors

  if (Array.isArray(errors)) {
    const firstMessage = errors.find(
      (validationError) => typeof validationError?.message === 'string'
    )?.message

    if (firstMessage) {
      return firstMessage
    }
  }

  return fallbackMessage
}

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  disabled = false,
  loading = false,
  onClick,
}) => (
  <button
    type="button"
    className={cx(
      'p-[16px] flex justify-center rounded-[100px] text-lg font-semibold relative overflow-hidden w-full',
      {
        'bg-dark-500 text-greyscale-700 pointer-events-none':
          disabled || loading,
        'bg-dark-100 text-white': !disabled && !loading,
      }
    )}
    disabled={disabled || loading}
    onClick={onClick}
  >
    {loading && (
      <div className="bg-dark-500 absolute top-0 right-0 bottom-0 left-0 z-10" />
    )}
    {loading && (
      <div className="loading absolute left-[50%] top-[50%] -translate-x-[50%] -translate-y-[50%] z-[20]">
        <span />
        <span />
        <span />
      </div>
    )}
    <span className={cx({ 'opacity-0': loading })}>{children}</span>
  </button>
)

const ForgotPassword: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [loading, setLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSeconds, setResendSeconds] = useState(0)
  const [errors, setErrors] = useState<ValidationError[]>([])
  const trimmedEmail = email.trim()
  const isEmailValid = EMAIL_REGEX.test(trimmedEmail)

  const stepParam = searchParams.get('step')
  const step = isStep(stepParam) ? stepParam : 'email'
  const currentStep = (() => {
    if (!email && step !== 'email') {
      return 'email'
    }

    if (step === 'reset' && !otp.trim()) {
      return 'otp'
    }

    return step
  })()
  const isOtpStep = currentStep === 'otp'
  const isResetStep = currentStep === 'reset'
  const loginParams = new URLSearchParams()

  if (searchParams.get('capsule')) {
    loginParams.set('capsule', searchParams.get('capsule')!)
  }

  if (searchParams.get('event')) {
    loginParams.set('event', searchParams.get('event')!)
  }

  const loginUrl = `/login${
    loginParams.toString() ? `?${loginParams.toString()}` : ''
  }`

  const setStep = (nextStep: Step) => {
    const nextSearchParams = new URLSearchParams(searchParams)

    if (nextStep === 'email') {
      nextSearchParams.delete('step')
    } else {
      nextSearchParams.set('step', nextStep)
    }

    setSearchParams(nextSearchParams)
  }

  const setFieldError = (field: string, message: string, rule: string) => ({
    field,
    message,
    rule,
  })

  const validateEmailStep = () => {
    const nextErrors: ValidationError[] = []
    const trimmedEmail = email.trim()

    if (!trimmedEmail) {
      nextErrors.push(
        setFieldError('email', 'E-posta adresi gereklidir.', 'required')
      )
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      nextErrors.push(
        setFieldError('email', 'Geçerli bir e-posta adresi girin.', 'email')
      )
    }

    return nextErrors
  }

  const validateResetStep = () => {
    const nextErrors = validateEmailStep()
    const normalizedPassword = password.trim()
    const normalizedPasswordConfirmation = passwordConfirmation.trim()

    if (!otp.trim()) {
      nextErrors.push(setFieldError('otp', 'Kod gereklidir.', 'required'))
    }

    if (!normalizedPassword) {
      nextErrors.push(
        setFieldError('password', 'Şifre gereklidir.', 'required')
      )
    }

    if (!normalizedPasswordConfirmation) {
      nextErrors.push(
        setFieldError(
          'password_confirmation',
          'Şifre tekrarı gereklidir.',
          'required'
        )
      )
    }

    if (
      normalizedPassword &&
      normalizedPasswordConfirmation &&
      normalizedPassword !== normalizedPasswordConfirmation
    ) {
      nextErrors.push(
        setFieldError('password_confirmation', 'Şifreler eşleşmiyor.', 'same')
      )
    }

    return nextErrors
  }

  const handleRequestError = (error: AxiosError<ApiErrorResponse>) => {
    const validationErrors = getApiValidationErrors(error)
    const fieldErrors = validationErrors.filter(
      (validationError) => validationError.field !== 'general'
    )
    const generalError = validationErrors.find(
      (validationError) => validationError.field === 'general'
    )

    setErrors(fieldErrors)

    const message =
      generalError?.message ||
      (!fieldErrors.length ? getApiErrorMessage(error) : undefined)

    if (message) {
      toast.dismiss()
      toast.error(message)
    }
  }

  const onSubmit = async () => {
    const validationErrors = validateEmailStep()

    setErrors(validationErrors)

    if (validationErrors.length || loading) {
      return
    }

    setLoading(true)
    setErrors([])

    try {
      const trimmedEmail = email.trim()

      await post('/forgot-password', { email: trimmedEmail })

      setEmail(trimmedEmail)
      setOtp('')
      setPassword('')
      setPasswordConfirmation('')
      setResendSeconds(RESEND_SECONDS)
      setStep('otp')
    } catch (e) {
      const error = e as AxiosError<ApiErrorResponse>

      if (error.response) {
        handleRequestError(error)
      } else {
        toast.dismiss()
        toast.error('Bir hata oluştu')
      }
    } finally {
      setLoading(false)
    }
  }

  const onResetPassword = async () => {
    const validationErrors = validateResetStep()

    setErrors(validationErrors)

    if (validationErrors.length || resetLoading) {
      return
    }

    setResetLoading(true)
    setErrors([])

    try {
      await post('/reset-password', {
        email: email.trim(),
        otp: otp.trim(),
        password,
        password_confirmation: passwordConfirmation,
      })

      setOtp('')
      setPassword('')
      setPasswordConfirmation('')
      setStep('success')

      toast.dismiss()
      toast.success('Şifreniz başarıyla güncellendi.')
    } catch (e) {
      const error = e as AxiosError<ApiErrorResponse>

      if (error.response) {
        handleRequestError(error)
      } else {
        toast.dismiss()
        toast.error('Bir hata oluştu')
      }
    } finally {
      setResetLoading(false)
    }
  }

  const onContinueToReset = () => {
    const nextErrors = validateEmailStep()

    if (!otp.trim()) {
      nextErrors.push(setFieldError('otp', 'Kod gereklidir.', 'required'))
    }

    setErrors(nextErrors)

    if (nextErrors.length) {
      return
    }

    setErrors([])
    setStep('reset')
  }

  const onResendCode = async () => {
    if (resendLoading || !email.trim()) {
      return
    }

    setResendLoading(true)

    try {
      await post('/forgot-password', { email: email.trim() })

      setOtp('')
      setResendSeconds(RESEND_SECONDS)
      setErrors((currentErrors) =>
        currentErrors.filter((error) => error.field !== 'otp')
      )

      toast.dismiss()
      toast.success('Kod başarıyla gönderildi.')
    } catch (e) {
      const error = e as AxiosError<ApiErrorResponse>

      if (error.response) {
        handleRequestError(error)
      } else {
        toast.dismiss()
        toast.error('Bir hata oluştu')
      }
    } finally {
      setResendLoading(false)
    }
  }

  const onChangeEmail = () => {
    setErrors([])
    setOtp('')
    setPassword('')
    setPasswordConfirmation('')
    setResendSeconds(0)
    setStep('email')
  }

  const onEmailInputChange = (value: string) => {
    setEmail(value)
    setErrors((currentErrors) =>
      currentErrors.filter((error) => error.field !== 'email')
    )
  }

  const onEmailBlur = () => {
    if (!trimmedEmail) {
      setErrors((currentErrors) => [
        ...currentErrors.filter((error) => error.field !== 'email'),
        setFieldError('email', 'E-posta adresi gereklidir.', 'required'),
      ])
      return
    }

    if (!isEmailValid) {
      setErrors((currentErrors) => [
        ...currentErrors.filter((error) => error.field !== 'email'),
        setFieldError('email', 'Geçerli bir e-posta adresi girin.', 'email'),
      ])
    }
  }

  const onChangeOtp = (value: string) => {
    setOtp(value.replace(/\s/g, ''))
  }

  const onChangePassword = (value: string) => {
    setPassword(value)
    setErrors((currentErrors) =>
      currentErrors.filter(
        (error) =>
          error.field !== 'password' && error.field !== 'password_confirmation'
      )
    )
  }

  const onChangePasswordConfirmation = (value: string) => {
    setPasswordConfirmation(value)
    setErrors((currentErrors) =>
      currentErrors.filter(
        (error) =>
          error.field !== 'password' && error.field !== 'password_confirmation'
      )
    )
  }

  useEffect(() => {
    if ((!isOtpStep && !isResetStep) || resendSeconds === 0) {
      return
    }

    const timeout = window.setTimeout(() => {
      setResendSeconds((seconds) => Math.max(seconds - 1, 0))
    }, 1000)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [isOtpStep, isResetStep, resendSeconds])

  if (currentStep === 'success') {
    return (
      <div className="flex flex-col h-full">
        <div className="pt-[32px] px-[24px] overflow-scroll flex-1">
          <div className="mb-[32px]">
            <div className="text-greyscale-900 text-h3 font-semibold mb-[16px]">
              Şifren güncellendi ✅
            </div>
            <div className="text-greyscale-700 text-h6 font-medium">
              Yeni şifren ile giriş yapabilirsin.
            </div>
          </div>
        </div>

        <div className="p-[24px] pb-[32px] border-t border-t-greyscale-100">
          <Link
            to={loginUrl}
            className="p-[16px] flex justify-center rounded-[100px] text-lg font-semibold bg-dark-100 text-white"
          >
            Giriş Yap
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="pt-[32px] px-[24px] overflow-scroll flex-1">
        <div className="mb-[32px]">
          <div className="text-greyscale-900 text-h3 font-semibold mb-[16px]">
            Şifreni sıfırla
          </div>
          <div className="text-greyscale-700 text-h6 font-medium">
            {currentStep === 'email' &&
              'Lütfen e-posta adresini gir. Şifreni sıfırlamak için sana tek kullanımlık bir kod göndereceğiz.'}
            {isOtpStep &&
              'E-postana gönderilen tek kullanımlık kodu girerek yeni şifreni belirleme adımına geç.'}
            {isResetStep &&
              'E-postana gelen kod ve yeni şifren ile şifreni sıfırlayabilirsin.'}
          </div>
        </div>

        <div className="flex flex-col gap-[28px]">
          <div className="flex flex-col gap-[24px]">
            <Input
              value={email}
              onChange={onEmailInputChange}
              onBlur={onEmailBlur}
              placeholder="E-posta adresi"
              label="E-posta adresi"
              htmlType="email"
              error={getError(errors, 'email')}
              readOnly={isOtpStep || isResetStep}
            />

            {(isOtpStep || isResetStep) && (
              <>
                <button
                  type="button"
                  className="text-xl text-secondary-800 font-semibold self-end"
                  onClick={onChangeEmail}
                >
                  E-postayı değiştir
                </button>

                <Input
                  value={otp}
                  onChange={onChangeOtp}
                  placeholder="Tek kullanımlık kod"
                  label="Kod"
                  htmlType="tel"
                  error={getError(errors, 'otp')}
                />

                {isResetStep && (
                  <>
                    <Input
                      value={password}
                      onChange={onChangePassword}
                      placeholder="Yeni şifre"
                      label="Yeni şifre"
                      htmlType="password"
                      error={getError(errors, 'password')}
                    />

                    <Input
                      value={passwordConfirmation}
                      onChange={onChangePasswordConfirmation}
                      placeholder="Yeni şifre tekrar"
                      label="Yeni şifre tekrar"
                      htmlType="password"
                      error={getError(errors, 'password_confirmation')}
                    />
                  </>
                )}

                {resendSeconds > 0 && (
                  <div className="text-lg text-greyscale-900 font-semibold text-center">
                    Kodu {resendSeconds} saniye içinde yeniden
                    gönderebilirsiniz.
                  </div>
                )}

                {resendSeconds === 0 && (
                  <ActionButton loading={resendLoading} onClick={onResendCode}>
                    Kodu yeniden gönder
                  </ActionButton>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-[24px] pb-[32px] border-t border-t-greyscale-100">
        {currentStep === 'email' && (
          <ActionButton
            disabled={!trimmedEmail || !isEmailValid}
            loading={loading}
            onClick={onSubmit}
          >
            Devam Et
          </ActionButton>
        )}

        {isOtpStep && (
          <ActionButton disabled={!otp.trim()} onClick={onContinueToReset}>
            Devam Et
          </ActionButton>
        )}

        {isResetStep && (
          <ActionButton
            disabled={!otp.trim() || !password || !passwordConfirmation}
            loading={resetLoading}
            onClick={onResetPassword}
          >
            Şifremi Sıfırla
          </ActionButton>
        )}
      </div>
    </div>
  )
}

export default ForgotPassword
